import React, { useState } from 'react'
import '../styles/StarRating.css'

interface StarRatingProps {
  value: number
  onChange?: (value: number) => void
  size?: 'sm' | 'md' | 'lg'
  readOnly?: boolean
  ariaLabel?: string
}

const SIZE_PX: Record<NonNullable<StarRatingProps['size']>, number> = {
  sm: 18,
  md: 26,
  lg: 34
}

export const StarRating: React.FC<StarRatingProps> = ({
  value,
  onChange,
  size = 'md',
  readOnly = false,
  ariaLabel = 'Rating'
}) => {
  const [hovered, setHovered] = useState<number | null>(null)
  const fontSize = SIZE_PX[size]
  const display = hovered ?? value

  return (
    <div
      className={`star-rating star-rating-${size} ${readOnly ? 'is-readonly' : ''}`.trim()}
      role={readOnly ? 'img' : 'radiogroup'}
      aria-label={`${ariaLabel}: ${value} of 5`}
      style={{ fontSize }}
      onMouseLeave={() => setHovered(null)}
    >
      {[1, 2, 3, 4, 5].map(star => {
        const filled = star <= display
        const className = `star-rating-star ${filled ? 'is-filled' : ''}`.trim()

        if (readOnly) {
          return (
            <span key={star} className={className} aria-hidden="true">★</span>
          )
        }

        return (
          <button
            key={star}
            type="button"
            role="radio"
            aria-checked={star === value}
            aria-label={`${star} star${star === 1 ? '' : 's'}`}
            className={className}
            onMouseEnter={() => setHovered(star)}
            onFocus={() => setHovered(star)}
            onBlur={() => setHovered(null)}
            onClick={() => onChange?.(star)}
          >
            ★
          </button>
        )
      })}
    </div>
  )
}
